import React, { useContext, useEffect} from 'react';
import { Box, Layer, TextInput, Anchor, Button, Select } from 'grommet';

import { propertyDetail, edit } from '../../../../api';

import { Placeholder, Loading, Container, Title, Alert, SubTitle } from '../../../../components';
import { TitleField, ZipcodeInput, CurrencyInput } from '../../../../components/form';

import { AppContext } from '../../../../context';
import { useForm, useFetchDetail, useCloseLayer } from '../../../../hooks';

import { preprocessorSend, formatEditObject } from '../../utils';

import { layers, entities, propertyUsageType, propertyUsage, propertyStatus, messageDictionary } from '../../../../constants';

const PropertyEdit = () => {
  const { close } = useCloseLayer();
  const { layerVisible, reloadFetch } = useContext(AppContext);

  const { loading, detailData, requestError, retryRequest } = useFetchDetail({
    get: propertyDetail,
    layerType: layers.edit
  });

  const { values, errors, pristine, submitting, handleSubmit, changeValues, initialize, resetEvents } = useForm();

  useEffect(() => {
    if (!layerVisible || !detailData) return;
  
    initialize(formatEditObject(detailData));
  }, [layerVisible, detailData, initialize]); 

  if(!layerVisible || layerVisible !== layers.edit)
    return null;

  return (
    <Layer
      responsive={false}
      full='vertical'
      position='left'
      onClickOutside={close}
      onEsc={close}
    >
      <Box
        fill='vertical'
        width='60vw'
        overflow='auto'
        flex
      >
        {requestError ? (
          <Placeholder
            title='Ops.'
            message={messageDictionary.requestFail}
            button={{
              label: 'Tentar novamente',
              action: retryRequest,
            }}
          />
        ) : null}

        {loading && !requestError ? (<Loading />) : null}

        {!loading && !requestError ? (
          <Container>
            <Title value='Editar imóvel' />

            {/* finalidade e tipo */}
            <Box
              direction='row'
              gap='small'
              pad={{ vertical: 'small' }}
            >
              <Box width='large'>
                <TitleField text='Finalidade' required />

                <Select
                  options={propertyUsage}
                  value={values.usage_id?.toString() ?? ''}
                  labelKey='name'
                  valueKey={{
                    key: 'id',
                    reduce: true,
                  }}
                  name='usage_id'
                  emptySearchMessage='Selecione uma opção'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>

              <Box width='large'>
                <TitleField text='Tipo' required />

                <Select
                  options={propertyUsageType}
                  value={values.usage_type_id?.toString() ?? ''}
                  labelKey='name'
                  valueKey={{
                    key: 'id',
                    reduce: true,
                  }}
                  name='usage_type_id'
                  emptySearchMessage='Selecione uma opção'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>
            </Box>


            {/* status */}
            <Box
              direction='row'
              gap='small'
              pad={{ vertical: 'small' }}
            >
              <Box width='large' fill='horizontal'>
                <TitleField text='Status' required />

                <Select
                  options={propertyStatus}
                  value={values.status_id?.toString() ?? ''}
                  labelKey='name'
                  valueKey={{
                    key: 'id',
                    reduce: true,
                  }}
                  name='status_id'
                  emptySearchMessage='Selecione uma opção'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>
            </Box>


            <SubTitle text='Valores' />

            {/* valores venda / locacao */}
            <Box
              direction='row'
              gap='small'
              pad={{ vertical: 'small' }}
            >
              <Box width='large'>
                <TitleField text='Valor de venda' />

                <CurrencyInput
                  maxLength={17}
                  value={values.sale_price ?? 0}
                  name='sale_price'
                  onChange={changeValues}
                />
              </Box>

              <Box width='large'>
                <TitleField text='Valor de locação' />

                <CurrencyInput
                  maxLength={17}
                  value={values.rent_price ?? 0}
                  name='rent_price'
                  onChange={changeValues}
                />
              </Box>
            </Box>


            <SubTitle text='Informações Adicionais' />

            {/* quadra, cartorio, comissao */}
            <Box
              direction='row'
              gap='small'
              pad={{ vertical: 'small' }}
            >
              <Box width='large'>
                <TitleField text='Quadra' />

                <TextInput
                  maxLength={50}
                  value={values.tx_quadra ?? ''}
                  name='tx_quadra'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>

              <Box width='large'>
                <TitleField text='Cartório (ID)' />

                <TextInput
                  type='number'
                  value={values.id_cartorio ?? ''}
                  name='id_cartorio'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>

              <Box width='large'>
                <TitleField text='Comissão (%)' />

                <TextInput
                  type='number'
                  step='0.01'
                  max='100'
                  value={values.dl_comissao ?? ''}
                  name='dl_comissao'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>
            </Box>


            <SubTitle text='Endereço' />

            <Alert
              alertType='warning'
              message={messageDictionary.warningZipcode}
            />

            {/* logradouro */}
            <Box
              direction='row'
              gap='small'
              pad={{ vertical: 'small' }}
            >
              <Box width='large' fill='horizontal'>
                <TitleField text='Logradouro' required />

                <TextInput
                  maxLength={200}
                  value={values.location_street_address ?? ''}
                  name='location_street_address'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>
            </Box>


            {/* número e complemento */}
            <Box
              direction='row'
              gap='small'
              pad={{ vertical: 'small' }}
            >
              <Box width='large'>
                <TitleField text='Número' required />

                <TextInput
                  type='number'
                  maxLength={4}
                  min={0}
                  value={values.location_street_number ?? ''}
                  name='location_street_number'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>

              <Box width='medium'>
                <TitleField text='Complemento' />

                <TextInput
                  maxLength={64}
                  value={values.location_add_on_address ?? ''}
                  name='location_add_on_address'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>
            </Box>


             {/* cidade */}
             <Box
              direction='row'
              gap='small'
              margin={{ bottom: 'small' }}
            >
              <Box width='large' fill='horizontal'>
                <TitleField text='Cidade' required />

                <TextInput
                  maxLength={200}
                  value={values.location_city ?? ''}
                  name='location_city'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>
            </Box>


             {/* bairro */}
             <Box
              direction='row'
              gap='small'
              margin={{ bottom: 'small' }}
            >
              <Box width='large' fill='horizontal'>
                <TitleField text='Bairro' required />

                <TextInput
                  maxLength={200}
                  value={values.location_neighborhood ?? ''}
                  name='location_neighborhood'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>
            </Box>


            {/* cep */}
            <Box
              direction='row'
              gap='small'
              margin={{ bottom: 'small' }}
            >
              <Box width='large' fill='horizontal'>
                <TitleField text='CEP' required />

                <ZipcodeInput
                  value={values.location_zipcode ?? ''}
                  name='location_zipcode'
                  onChange={({ target }) => changeValues(target.name, target.value)}
                />
              </Box>
            </Box>


            {errors && <Alert alertType='error' message={messageDictionary.requestFail} />}

            {/* botões */}
            <Box
              direction='row'
              pad={{ vertical: 'medium' }}
              justify='center'
              align='center'
              gap='small'
            >
              <Box width='xsmall'>
                <Anchor
                  color='dark-3'
                  label='Cancelar'
                  onClick={() => {
                    close();
                    resetEvents();
                  }}
                />
              </Box>

              <Box width='small'>
                <Button
                  color='brand'
                  primary
                  label={!submitting ? 'Atualizar' : 'Atualizando...'}
                  disabled={pristine || submitting}
                  onClick={() =>
                    handleSubmit({
                      resource: edit({ entity: entities.property, payload: preprocessorSend(values) }),
                      callbackSuccess: () =>
                        new Promise(
                          () => setTimeout(() => {
                            close();
                            reloadFetch();
                          }, 100)
                        )
                    })
                  }
                />
              </Box>
            </Box>
          </Container>
        ) : null}
      </Box>
    </Layer>
  );
}

export default PropertyEdit;
